import { NgClass } from '@angular/common';
import { Component, OnInit } from '@angular/core';
import { MatExpansionModule } from '@angular/material/expansion';
import { faFileAlt } from '@fortawesome/free-solid-svg-icons';
import { NgxSpinnerService } from 'ngx-spinner';
import { Observable } from 'rxjs';
import { FormMode } from 'src/app/controls/common/form-mode';
import { DefaultManagementComponent } from 'src/app/controls/forms/default-management.component';
import { HttpError } from 'src/app/core/http-error';
import { Box } from 'src/app/models/box';
import { DocumentType } from 'src/app/models/document-type';
import { Rights } from 'src/app/models/enums/rights';
import { IDocumentType } from 'src/app/models/interfaces/document-type.interface';
import { BoxesService } from 'src/app/services/boxes.service';
import { DocumentTypesService } from 'src/app/services/document-type.service';
import { MemoryService } from 'src/app/services/memory.service';
import { NotificationService } from 'src/app/services/notification-service';
import { DetailHeaderComponent } from '../../controls/detail-header/detail-header.component';
import { ListHeaderComponent } from '../../controls/list-header/list-header.component';
import { DocumentTypeDetailContext } from './document-type-detail-context';
import { DocumentTypeDetailComponent } from './document-type-detail/document-type-detail.component';
import { DocumentTypeFormContext } from './document-type-form-context';
import { DocumentTypesListComponent } from './document-types-list/document-types-list.component';

enum DisplayMode {
    List,
    Form,
}
@Component({
    selector: 'app-document-types',
    templateUrl: './document-types.component.html',
    styleUrls: ['./document-types.component.scss'],
    imports: [MatExpansionModule, NgClass, ListHeaderComponent, DocumentTypesListComponent, DetailHeaderComponent, DocumentTypeDetailComponent],
})
export class DocumentTypesComponent extends DefaultManagementComponent<IDocumentType, DocumentType, number> implements OnInit {
    /**
     * Liste des icônes utilisés dans le template
     */
    public faDocumentType = faFileAlt;

    /**
     * Liste des enums utilisés dans le template
     */
    public override FormMode = FormMode;
    public override DisplayMode = DisplayMode;
    /**
     * Contexte par défaut du formulaire contenant les données invariantes entre clients.
     */
    public defaultContext: DocumentTypeDetailContext = new DocumentTypeDetailContext();

    /**
     * Liste des bannettes de l'organisation courante.
     */
    boxes: Box[];

    /**
     * Indique si le bouton d'ajout/Edit doit apparaitre.
     */
    public displayAddbuttom = true;
    public displayEditbuttom = true;

    /**
     *
     * @param notificationService : Service utilisé pour gérer l'affichage des notifications.
     * @param DocumentTypesService : Service métier utilisé pour gérer les types de documents.
     */
    constructor(
        protected override notificationService: NotificationService,
        private documentTypesService: DocumentTypesService,
        private boxesService: BoxesService,
        private memoryService: MemoryService,
        private spinner: NgxSpinnerService
    ) {
        super(notificationService);
    }

    /**
     * Appellé lors de l'initialisation du composant.
     */
    ngOnInit() {
        this.displayAddbuttom = !!this.memoryService.getConnectedUser().hasRight(Rights.DocumentTypeCreate);
        this.displayEditbuttom = !!this.memoryService.getConnectedUser().hasRight(Rights.DocumentTypeEdit);
        this.loadBoxes();
    }

    /**
     * Appellé lorsque l'utilisateur selectionne un client.
     * @param documentType Client selectionné.
     */
    public onDocumentTypeSelected(documentType: DocumentType) {
        this.spinner.show('main-spinner');
        this.createContext();
        this.loadDocumentType(documentType.id).subscribe(
            (x) => {
                this.refresh(DocumentType.createFormCopy(x), FormMode.Reading);
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', "Impossible d'afficher le type de document." + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Appellé lorsque l'utilisateur clique sur l'icône 'Retour à la liste'.
     */
    public onBackToList() {
        this.setDisplayMode(DisplayMode.List);
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Consulter' dans le menu contextuel.
     * @param documentType Type de document sélectionné.
     */
    public onDocumentTypeDisplayed(documentType: DocumentType) {
        this.spinner.show('main-spinner');
        this.createContext();
        this.loadDocumentType(documentType.id).subscribe(
            (x) => {
                this.refresh(DocumentType.createFormCopy(x), FormMode.Reading);
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', "Impossible d'afficher le type de document." + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Editer' dans le menu contextuel.
     * @param documentType Type de document sélectionné.
     */
    public onDocumentTypeEdited(documentType: DocumentType) {
        this.spinner.show('main-spinner');
        this.createContext();
        // Cas de la mise à jour.
        this.loadDocumentType(documentType.id).subscribe(
            (x) => {
                this.refresh(DocumentType.createFormCopy(x), FormMode.Edition);
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', "Impossible d'éditer le type de document." + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Appellé lorsque l'utilisateur clique sur le bouton 'Créer'.
     */
    public onDocumentTypeCreated() {
        this.displayForm(DocumentType.createFormCopy(new DocumentType()), FormMode.Creation);
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Dupliquer' dans le menu contextuel.
     * @param documentType Type de document sélectionné.
     */
    public onDocumentTypeDuplicated(documentType: DocumentType) {
        let duplicatedDocumentType = DocumentType.createFormCopy(documentType);
        duplicatedDocumentType.id = null;
        this.displayForm(duplicatedDocumentType, FormMode.Creation);
    }

    /**
     * Appellé lorsque le contenu du formulaire a été sauvegardé.
     * @param DocumentType : Valeur du type de document retourné par le formulaire.
     */
    public onDocumentTypeUpdated(documentType: DocumentType): void {
        this.spinner.show('main-spinner');
        if (this.context.editedItemId) {
            // Cas de la mise à jour.
            this.documentTypesService.update(this.context.editedItemId, documentType).subscribe(
                (x) => {
                    this.refreshWithNotification(DocumentType.createFormCopy(x), 'Type de document mis à jour', FormMode.Reading);
                },
                (error: HttpError) => {
                    this.notificationService.displayError('Erreur', 'Impossible de mettre à jour le type de document.' + error.message);
                    this.spinner.hide('main-spinner');
                }
            );
        } else {
            // Cas de la création.
            this.documentTypesService.add(documentType).subscribe(
                (x) => {
                    this.refreshWithNotification(DocumentType.createFormCopy(x), 'Type de document crée', FormMode.Reading);
                },
                (error: HttpError) => {
                    this.notificationService.displayError('Erreur', 'Impossible de créer le type de document.' + error.message);
                    this.spinner.hide('main-spinner');
                }
            );
        }
    }

    /**
     * Appellé lorsque l'utilisateur sélectionne 'Supprimer' dans le menu contextuel.
     * @param documentType Type de document sélectionné.
     */
    public onDocumentTypeDeleted(documentType: DocumentType) {
        this.spinner.show('main-spinner');
        // Suppression effective du client.
        this.documentTypesService.delete(documentType.id).subscribe(
            (x) => {
                this.notificationService.displaySuccess(null, 'Type de document supprimé');
                this.itemsList.refresh();
                this.spinner.hide('main-spinner');
            },
            (error: HttpError) => {
                this.notificationService.displayError('Erreur', 'Impossible de supprimer le type de document.' + error.message);
                this.spinner.hide('main-spinner');
            }
        );
    }

    /**
     * Appellé lors de l'export
     */
    public onDocumentTypesExported() {
        this.createContext();
        this.itemsList.export('TypesDeDocument.xlsx');
    }

    /**
     * Appellé lors de l'import
     */
    public onDocumentTypesImported() {
        this.itemsList.refresh();
    }

    /**
     * Appellé lors de l'aide
     */
    public onDocumentTypesHelped() {
        this.itemsList.displayHelpDialog('documentTypes');
    }

    /**
     * Charge les données d'un client.
     * @param DocumentTypeId : Id du client à charger.
     */
    private loadDocumentType(documentTypeId: number): Observable<DocumentType> {
        return this.documentTypesService.get(documentTypeId);
    }

    /**
     * Charge la liste des bannettes proposés dans la liste déroulante bannette par défaut du formulaire.
     */
    private loadBoxes() {
        this.boxesService.ListForOrganisation(null, null, null, null, 0, 1000).subscribe((x) => {
            this.boxes = x.value;
        });
    }

    /**
     * Pré-rempli les champs du formulaire.
     * @param documentType : Valeur utilisée pour pré-remplir les champs du formulaire.
     */
    protected fillForm(documentType: DocumentType): DocumentTypeDetailContext {
        let context = this.createContext();
        context.formContext.item = documentType;
        context.editedItemId = documentType.id;

        return context;
    }

    /**
     * Créer un nouveau contexte.
     */
    protected createContext(): DocumentTypeDetailContext {
        let context = new DocumentTypeDetailContext();
        (context.formContext as DocumentTypeFormContext).boxes = this.boxes;
        return context;
    }
}
